import React, { useState, Fragment, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from '@reach/router';
import * as Yup from 'yup';
import { Button, FormErrors } from '@squirrellearningltd/go-pupil-components';
import { useMutation } from '@apollo/react-hooks';
import cx from 'classnames';
import styles from './Reading.module.scss';
import TextareaAutosize from 'react-textarea-autosize';
import { ADD_READING_BOOK_LOG } from '../../../../services/graphql';
import { useTranslation } from 'react-i18next';
import { AuthContext } from "../../../../../../services/user/AuthProvider";

const Reading = ({ studentBook }) => {
    Date.prototype.toDateInputValue = (function() {
        var local = new Date(this);
        local.setMinutes(this.getMinutes() - this.getTimezoneOffset());
        return local.toJSON().slice(0,10);
    });

    const { t, i18n } = useTranslation();
    const [formErrors, setFormErrors] = useState([]);
    const [logSaved, setLogSaved] = useState(false);
    const [thoughts, setThoughts] = useState('');
    const { currentUser, setCurrentUser } = useContext(AuthContext);

    const pageStart = !!studentBook.logs.length ? studentBook.logs.slice(-1)[0].pageEnd + 1 : 1

    const [addReadingBookLog, { loading }] = useMutation(ADD_READING_BOOK_LOG, {
        onCompleted: ({ addReadingBookLog }) => {
            if (addReadingBookLog.errors && addReadingBookLog.errors.length !== 0) {
                setFormErrors(addReadingBookLog.errors);
                return;
            }
            else if(addReadingBookLog.studentReadingBook.id && !!addReadingBookLog.studentReadingBook.logs.length) {
                setLogSaved(true);
                if (currentUser.isBoombassador) {
                    const updatedUser = {
                        ...currentUser,
                        boombassadorCounter: (currentUser.boombassadorCounter ?? 0) + 1,
                        dailyBoombassadorStats: {
                        ...currentUser.dailyBoombassadorStats,
                        dailyStats: (currentUser.dailyBoombassadorStats?.dailyStats ?? 0) + 1,
                        weeklyStats: (currentUser.dailyBoombassadorStats?.weeklyStats ?? 0) + 1,
                        yearlyStats: (currentUser.dailyBoombassadorStats?.yearlyStats ?? 0) + 1,
                        }
                    };

                    setCurrentUser(updatedUser);
                    }
            }
        },
    });

    const schema = Yup.object().shape({
        showEntryField: Yup.boolean(),
        pageStart: Yup.number().typeError(t('please_enter_number'))
            .min(1, t('page_start_min_validation'))
            .max(100000, t('page_start_max_validation')),
        pageEnd: Yup.number().typeError(t('please_enter_number'))
            .max(100000, t('page_end_max_validation')),   
        entry: Yup.string().when('showEntryField', {
            is: true,
            then: Yup.string()
          })
    });

    const { register, handleSubmit, errors } = useForm({
        validationSchema: schema
    });
    const onSubmit = values => {
        const { pageStart, pageEnd, entry, finished } = values;
        const currentDate = new Date();
        addReadingBookLog({
            variables: {
                studentReadingBookId: studentBook.id,
                readingDate: `${currentDate.toDateInputValue()} ${currentDate.toTimeString().slice(0,5)}:00`,
                pageStart,
                pageEnd,
                entry,
                finished,
                isBoombassador: window.location.pathname.includes("boombassador") && currentUser.isBoombassador
            },
        })
    }

    return(
        <Fragment>  
            {!logSaved ? (
                <form onSubmit={handleSubmit(onSubmit)} className={styles.logForm}>
                    <h3 className={cx('heavy', styles.logFormTitle)}>{t('log_your_reading')}</h3>
                    <p className={styles.logFormPrompt}>{t('what_page_have_you_read_up_to')}</p>
                    <div className={styles.logFormPages}>
                        <input
                            type="hidden"
                            name="showEntryField"
                            ref={register}
                            defaultValue={!studentBook.hideComments}
                        />
                        <input
                            name="pageStart"
                            className={cx('basic-form__text-box', styles.logFormPagesField)}
                            ref={register}
                            type="number"
                            defaultValue={pageStart}
                        />
                        <span className={styles.logFormPagesDivider}>{t('to')}</span>
                        <input
                            name="pageEnd"
                            className={cx('basic-form__text-box', styles.logFormPagesField)}
                            ref={register}
                            type="number"
                        />
                    </div>
                    {errors.pageStart && <p className="basic-form__hint">{errors.pageStart.message}</p>}
                    {errors.pageEnd && <p className="basic-form__hint">{errors.pageEnd.message}</p>}
                    {!studentBook.hideComments && (
                        <Fragment>
                            <p className={styles.logFormPrompt}>{t('what_do_you_think_of_what_you_read')}</p>
                            <div className={cx('basic-form__group', styles.logFormThoughtsGroup)}>
                                {!thoughts && (
                                    <span aria-hidden="true" className={styles.logFormThoughtsPlaceholder}>
                                        {t('give_us_your_thoughts')}
                                    </span>
                                )}
                                <TextareaAutosize
                                    aria-label={t('give_us_your_thoughts')}
                                    className={cx('basic-form__text-box', styles.logFormThoughts)}
                                    name="entry"
                                    minRows={4}
                                    onChange={event => setThoughts(event.target.value)}
                                    inputRef={register}
                                />
                            </div>
                            {errors.entry && <p className="basic-form__hint">{errors.entry.message}</p>}
                        </Fragment>
                    )}

                    <p className={styles.logFormPrompt}>{t('have_you_finished_the_book')}</p>
                    <div className={cx('basic-form__group', styles.logFormFinishedGroup)}>
                        <div className={cx("basic-form__switch", `basic_form__switch_${i18n.language}`)}>
                            <input
                                id="finished"
                                className="switch-input"
                                type="checkbox"
                                name="finished"
                                ref={register}
                            />
                            <label htmlFor="finished" className="switch-label"></label>
                        </div>
                    </div>

                    {formErrors.length !== 0 && <FormErrors errors={formErrors} />}
                    <Button className={styles.logFormSubmit} type="submit" primary>{loading ? t('saving') : t('add_reading_log')}</Button>
                </form>
            ) : (
                <div className={styles.logSaved}>
                    <div>
                        <h1 className='heavy'>{t('awesome')}</h1>
                        <p>{t('reading_log_saved')}</p>
                    </div>
                    <Link
                        className={styles.logSavedAction}
                        to={window.location.pathname.includes("boombassador") ? "/boombassador" : "/"}
                    >
                        <Button primary>{t('go_back_to_dashboard')}</Button>
                    </Link>
                </div>
            )}

        </Fragment>
    )
}

export default Reading;
